/**
 * What this build knows about its environment.
 *
 * The single place client code reads `import.meta.env`. Everything else imports
 * from here, so the rule below is stated once rather than being re-derived by
 * whoever next needs a value.
 *
 * **Everything in this module ships in the bundle and is readable by anyone.**
 * Only values that are public by construction belong here — an origin, an
 * environment name, a commit SHA. A credential added to this path is disclosed
 * with no error raised and no log line written, which is a worse failure than a
 * loud one. Anything that must stay secret belongs on the API, which reads its
 * own configuration from secrets it is given at deploy time.
 *
 * The values are embedded at build time by `vite.config.ts` from
 * `environments.json`; a static bundle has no server to ask, and the native
 * shell has no origin to ask it of.
 */

function required(value: string | undefined, variable: string): string {
  if (value === undefined || value.trim() === '') {
    throw new Error(
      `${variable} is empty, so this bundle does not know its own environment. ` +
        'It is embedded at build time from environments.json — see tools/web/client-config.ts. ' +
        'A bundle built outside the project build will not have it.',
    );
  }
  return value;
}

/**
 * A value that is legitimately absent.
 *
 * Distinct from `required` above rather than a flag on it, because the two mean
 * opposite things when a build is misconfigured: a missing origin is a bundle
 * that cannot work and must say so loudly, while a missing tracker credential
 * is a developer's machine and must work silently.
 */
function optional(value: string | null | undefined): string | null {
  if (value === undefined || value === null || value.trim() === '') return null;
  return value;
}

export interface ClientConfig {
  /** The origin of the API this build talks to. */
  readonly apiOrigin: string;
  /** The environment's name — the same string the API reports for itself. */
  readonly environment: string;
  /**
   * The release this bundle belongs to: the bare commit SHA.
   *
   * Byte-identical to the API's by construction, because both take it from the
   * same commit. An error tracker files events and source maps under a release
   * name, so two identifiers would leave the maps attached to a release no
   * event carries — and traces unreadable, with nothing reporting a problem.
   */
  readonly release: string;
  /**
   * The error tracker's credential, or `null` when reporting is not configured.
   *
   * Public by design — a DSN names a project and accepts ingestion, it reads
   * nothing — which is why it may live here at all. It is the one value not
   * derived from `environments.json`: the registry states what an environment
   * *is*, and a credential-shaped field there invites the next one.
   *
   * `null` is a working state, not a failure. It is what a developer's machine
   * has, and `../observability/init.ts` treats it as reporting being off.
   */
  readonly sentryDsn: string | null;
  /**
   * The Supabase project this build obtains its session from (HOM-61).
   *
   * The URL is derived from the same ref the API derives its trusted issuer
   * from; the key is the project's *publishable* key, which Supabase states is
   * safe in a web page, an app or source code — row-level security is what
   * protects the data. Public by construction, like the origin. The secret
   * key never belongs here or in the registry it comes from.
   */
  readonly supabaseUrl: string;
  readonly supabasePublishableKey: string;
}

export const clientConfig: ClientConfig = {
  apiOrigin: required(import.meta.env.VITE_HOM_API_ORIGIN, 'VITE_HOM_API_ORIGIN'),
  environment: required(import.meta.env.VITE_HOM_ENVIRONMENT, 'VITE_HOM_ENVIRONMENT'),
  release: required(import.meta.env.VITE_HOM_RELEASE, 'VITE_HOM_RELEASE'),
  sentryDsn: optional(import.meta.env.VITE_HOM_SENTRY_DSN),
  supabaseUrl: required(import.meta.env.VITE_HOM_SUPABASE_URL, 'VITE_HOM_SUPABASE_URL'),
  supabasePublishableKey: required(
    import.meta.env.VITE_HOM_SUPABASE_PUBLISHABLE_KEY,
    'VITE_HOM_SUPABASE_PUBLISHABLE_KEY',
  ),
};
